import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/tenantClient";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { getDefaultRouteForRole } from "@/hooks/useDefaultRoute";
import { useToast } from "@/hooks/use-toast";
import { useSSOPopup, isInIframe } from "@/hooks/useSSOPopup";
import { useSSOSecuritySettings } from "@/hooks/useSSOSecuritySettings";
import { useAuthRequest } from "@/hooks/useAuthRequest";
import { AuthCarousel } from "@/components/auth/AuthCarousel";
import { LoginForm } from "@/components/auth/LoginForm";
import { SSOLoginForm } from "@/components/auth/SSOLoginForm";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { ResetPasswordForm } from "@/components/auth/ResetPasswordForm";
import { MFAVerifyForm } from "@/components/auth/MFAVerifyForm";
import { MFASetupForm } from "@/components/auth/MFASetupForm";
import { SSOPopupOverlay } from "@/components/auth/SSOPopupOverlay";
import { Logo } from "@/components/common/Logo";
import { isLoginDomain } from "@/lib/domainUtils";
import { getPostAuthRedirectUrl } from "@/services/authRedirect";

type AuthState = 'login' | 'sso-login' | 'forgot-password' | 'reset-sent' | 'reset-password' | '2fa-verify' | '2fa-setup' | 'sso-callback';

export default function AuthPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { user, session, loading: authLoading } = useAuth();
  const { role, loading: roleLoading } = useUserRole();
  const { ssoBypass2FA, ssoBypassUsers, loading: ssoSettingsLoading } = useSSOSecuritySettings();
  const [authState, setAuthState] = useState<AuthState>('login');
  const [_userEmail, setUserEmail] = useState<string>("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [processingHashToken, setProcessingHashToken] = useState(false);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [resetRequestEmail, setResetRequestEmail] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // SSO Popup handling for iframe contexts
  const { openSSOPopup, isPopupOpen, result: ssoPopupResult, clearResult } = useSSOPopup();
  
  // PAR-Lite context resolution for cross-domain SSO redirects
  const { context: parContext } = useAuthRequest();
  
  /**
   * Get the effective redirect URI from PAR context or URL params.
   * This ensures SSO flows redirect back to the original workspace.
   */
  const getEffectiveRedirectUri = (): string | null => {
    // Priority 1: PAR context redirect URI (from cross-domain flow)
    if (parContext?.redirectUri) {
      console.log('[AuthPage] Using PAR redirect URI:', parContext.redirectUri);
      return parContext.redirectUri;
    }
    // Priority 2: URL search param
    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      console.log('[AuthPage] Using URL redirect param:', redirectParam);
      return redirectParam;
    }
    // No redirect URI available
    return null;
  };

  // Handle Supabase magic link hash fragments (from SSO redirect)
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    
    if (accessToken) {
      console.log('[AuthPage] Detected access_token in hash fragment, waiting for session...');
      setProcessingHashToken(true);
      
      // DO NOT clear the hash yet - let Supabase process it first!
      
      // Listen for auth state change to confirm session is established
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event: string, session: unknown) => {
        if (event === 'SIGNED_IN' && session) {
          console.log('[AuthPage] Session established from hash token');
          setProcessingHashToken(false);
          subscription.unsubscribe();
          // Now safe to clear the hash
          window.history.replaceState(null, '', window.location.pathname);
        }
      });
      
      // Timeout fallback - if session doesn't establish in 5 seconds
      const timeout = setTimeout(async () => {
        // Try manual session setting as fallback
        if (accessToken && refreshToken) {
          console.log('[AuthPage] Attempting manual session set...');
          try {
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (data.session) {
              console.log('[AuthPage] Manual session set successful');
              setProcessingHashToken(false);
              window.history.replaceState(null, '', window.location.pathname);
              subscription.unsubscribe();
              return;
            }
            
            if (error) {
              console.error('[AuthPage] Manual session set failed:', error);
            }
          } catch (e) {
            console.error('[AuthPage] Error setting session:', e);
          }
        }
        
        // Final check for session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          console.log('[AuthPage] Session found after timeout');
          setProcessingHashToken(false);
          window.history.replaceState(null, '', window.location.pathname);
        } else {
          console.error('[AuthPage] Failed to establish session from hash token');
          setProcessingHashToken(false);
          window.history.replaceState(null, '', window.location.pathname);
          toast({
            title: "Authentication Failed",
            description: "Could not establish session. Please try again.",
            variant: "destructive",
          });
        }
        subscription.unsubscribe();
      }, 5000);
      
      return () => {
        clearTimeout(timeout);
        subscription.unsubscribe();
      };
    }
  }, [toast]);

  // Handle SSO popup result - session is already established by the popup
  useEffect(() => {
    if (ssoPopupResult) {
      if (ssoPopupResult.success) {
        // Session was already established by the popup callback page
        // Refresh to pick up the session - auth state listener will handle redirect
        setAuthState('sso-callback');
        supabase.auth.getSession().then(({ data: { session: currentSession } }: { data: { session: unknown } }) => {
          if (currentSession) {
            // Session exists, trigger a re-check by updating state
            // The auth state change listener will handle the redirect
            setChecking(true);
          } else {
            // No session found, show error
            toast({
              title: "SSO Login Failed",
              description: "Session could not be established. Please try again.",
              variant: "destructive",
            });
            setAuthState('login');
          }
        });
      } else if (ssoPopupResult.error) {
        toast({
          title: "SSO Login Failed",
          description: ssoPopupResult.error,
          variant: "destructive",
        });
      }
      clearResult();
    }
  }, [ssoPopupResult, toast, clearResult]);

  // Check session and MFA status
  useEffect(() => {
    const checkAuth = async () => {
      if (authLoading) return;

      // No session - show login
      if (!session || !user) {
        // Check if this is a password recovery session
        const type = searchParams.get('type');
        const token = searchParams.get('token');
        
        if (type === 'recovery') {
          if (token) {
            // Verify the custom token
            try {
              const { data, error } = await supabase.functions.invoke('verify-reset-token', {
                body: { token }
              });
              
              if (error || !data?.valid) {
                toast({
                  title: "Invalid Reset Link",
                  description: data?.error || "This password reset link is invalid or has expired.",
                  variant: "destructive",
                });
                setAuthState('login');
                setChecking(false);
                return;
              }
              
              // Token is valid, show reset form with email
              setResetToken(token);
              setResetEmail(data.email);
              setAuthState('reset-password');
            } catch (err) {
              console.error('[AuthPage] Error verifying reset token:', err);
              toast({
                title: "Error",
                description: "Failed to verify reset link. Please try again.",
                variant: "destructive",
              });
              setAuthState('login');
            }
          } else {
            // No token - redirect to forgot password with error
            toast({
              title: "Invalid Reset Link",
              description: "Please request a new password reset link.",
              variant: "destructive",
            });
            setAuthState('forgot-password');
          }
        }
        setChecking(false);
        return;
      }

      try {
        // Check MFA assurance level
        const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        
        if (aalError) {
          console.error("Error getting AAL:", aalError);
          setChecking(false);
          return;
        }

        // Check MFA factors
        const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();
        
        if (factorsError) {
          console.error("Error listing factors:", factorsError);
          setChecking(false);
          return;
        }

        const verifiedFactor = factors.totp.find((f: { status: string }) => f.status === 'verified');

        // Check if this is an SSO session that should bypass 2FA entirely
        // Check BOTH user metadata (persistent) AND sessionStorage (fallback) for SSO session
        const isSSOSession = 
          user?.user_metadata?.sso_session === true ||
          sessionStorage.getItem('sso_authenticated') === 'true';
        if (isSSOSession && ssoBypass2FA) {
          const canBypass = ssoBypassUsers.length === 0 || ssoBypassUsers.includes(user.id);
          if (canBypass) {
            console.log('[AuthPage] SSO session with 2FA bypass enabled, skipping MFA checks');
            // Skip both 2FA verification AND 2FA setup - redirect directly to workspace
            if (!roleLoading) {
              await performPostAuthRedirect(user.id);
            }
            setChecking(false);
            return;
          }
        }

        // User has verified MFA but hasn't completed it for this session
        if (verifiedFactor && aalData.currentLevel !== 'aal2') {
          setFactorId(verifiedFactor.id);
          setAuthState('2fa-verify');
          setChecking(false);
          return;
        }

        // User is fully authenticated (no MFA or MFA completed)
        if (aalData.currentLevel === 'aal2' || !verifiedFactor) {
          // If no MFA enrolled, show setup prompt
          if (!verifiedFactor) {
            setAuthState('2fa-setup');
            setChecking(false);
            return;
          }
          
          // Fully authenticated - redirect
          if (!roleLoading) {
            await performPostAuthRedirect(user.id);
          }
        }
      } catch (error) {
        console.error("Auth check error:", error);
      }
      setChecking(false);
    };

    checkAuth();
  }, [authLoading, roleLoading, session, user, role, navigate, searchParams, ssoBypass2FA, ssoBypassUsers]);

  /**
   * Perform post-authentication redirect.
   * On login domain, redirect to user's target workspace with tokens.
   * On other domains, navigate locally.
   */
  const performPostAuthRedirect = async (userId: string) => {
    // On login domain, perform cross-domain redirect with tokens
    if (isLoginDomain()) {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          const redirectInfo = await getPostAuthRedirectUrl(userId, searchParams);
          
          // Build hash params using URLSearchParams for proper encoding
          const hashParams = new URLSearchParams();
          hashParams.set('access_token', sessionData.session.access_token);
          hashParams.set('refresh_token', sessionData.session.refresh_token);
          
          // If SSO session, pass that along
          if (sessionStorage.getItem('sso_authenticated') === 'true') {
            hashParams.set('sso_session', 'true');
          }
          
          // Preserve popup mode params for cross-origin iframe flow
          // These params are set by useLoginPopup when opening the popup
          const popupMode = searchParams.get('popup_mode');
          const openerOrigin = searchParams.get('opener_origin');
          if (popupMode === 'true') {
            hashParams.set('popup_mode', 'true');
            if (openerOrigin) {
              hashParams.set('opener_origin', openerOrigin);
            }
          }
          
          // Build redirect URL with tokens in hash
          const transferUrl = new URL(redirectInfo.url);
          transferUrl.hash = hashParams.toString();
          
          console.log('[AuthPage] Cross-domain redirect to:', redirectInfo.subdomain, 
            popupMode === 'true' ? '(popup mode)' : '');
          
          // CRITICAL FIX: Clear login-dev session storage directly WITHOUT calling signOut
          // signOut() makes a server call that invalidates tokens - we must avoid that
          // The JWT tokens remain valid until expiry, we just need to prevent auto-detection
          
          // Construct the storage key used by Supabase client
          const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID || 'rqmmbnklhfmbxkmgroso';
          const storageKey = `sb-${projectId}-auth-token`;
          
          // Remove Supabase auth token from sessionStorage
          sessionStorage.removeItem(storageKey);
          
          // Clear SSO session markers
          sessionStorage.removeItem('sso_authenticated');
          sessionStorage.removeItem('sso_config_id');
          
          console.log('[AuthPage] Cleared login-dev session storage (no server call)');
          
          // Now redirect with the valid tokens
          window.location.href = transferUrl.toString();
          return;
        }
      } catch (e) {
        console.error('[AuthPage] Error during cross-domain redirect:', e);
      }
    }
    
    // On tenant/UAT domain or development, navigate locally
    navigate(getDefaultRouteForRole(role), { replace: true });
  };

  const handleLoginSuccess = async (email: string) => {
    setUserEmail(email);
    
    // Check MFA status after login
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const verifiedFactor = factors?.totp.find((f: { status: string }) => f.status === 'verified');
    
    if (verifiedFactor) {
      setFactorId(verifiedFactor.id);
      setAuthState('2fa-verify');
    } else {
      // No MFA - show setup prompt
      setAuthState('2fa-setup');
    }
  };

  const handleMFAVerifySuccess = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await performPostAuthRedirect(user.id);
    } else {
      navigate(getDefaultRouteForRole(role), { replace: true });
    }
  };

  const handleMFASetupComplete = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await performPostAuthRedirect(user.id);
    } else {
      navigate(getDefaultRouteForRole(role), { replace: true });
    }
  };

  const handleMFASkip = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      await performPostAuthRedirect(user.id);
    } else {
      navigate(getDefaultRouteForRole(role), { replace: true });
    }
  };

  const handleResetSent = (email: string) => {
    setResetRequestEmail(email);
    setAuthState('reset-sent');
    setResendCooldown(30); // Start 30 second cooldown
  };

  const handleResendResetEmail = async () => {
    if (!resetRequestEmail || resendLoading || resendCooldown > 0) return;
    
    setResendLoading(true);
    try {
      await supabase.functions.invoke('send-password-reset-email', {
        body: { email: resetRequestEmail }
      });
      
      // Start cooldown
      setResendCooldown(30);
      toast({
        title: "Email sent",
        description: "A new password reset link has been sent to your email.",
      });
    } catch (err) {
      // Still show success to prevent enumeration
      setResendCooldown(30);
    } finally {
      setResendLoading(false);
    }
  };

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handlePasswordResetSuccess = () => {
    // Clear reset token state
    setResetToken(null);
    setResetEmail(null);
    setAuthState('login');
  };

  const handleBackToLogin = () => {
    setAuthState('login');
  };

  const handleSSORedirect = (redirectUrl: string) => {
    // Check if we're in an iframe (e.g., Lovable preview)
    if (isInIframe()) {
      // Use popup for SSO when in iframe context
      openSSOPopup(redirectUrl);
    } else {
      // Normal redirect for standalone browser
      window.location.href = redirectUrl;
    }
  };

  // Show loading while checking auth or during SSO callback
  if (checking || authLoading || authState === 'sso-callback' || processingHashToken || ssoSettingsLoading) {
    return (
      <>
        <SSOPopupOverlay isOpen={isPopupOpen} />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            {(authState === 'sso-callback' || processingHashToken) && (
              <p className="text-muted-foreground text-sm">Completing sign-in...</p>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* SSO Popup Overlay - shown when waiting for popup authentication */}
      <SSOPopupOverlay isOpen={isPopupOpen} />
      
      <div className="min-h-screen flex bg-background">
        {/* Left Panel - Carousel (hidden on mobile) */}
        <div className="hidden md:flex md:w-1/2 relative">
          <AuthCarousel />
        </div>

        {/* Right Panel - Auth Forms */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-6 md:p-12 relative overflow-hidden">
        {/* Dark background with cyan bottom glow */}
        <div className="absolute inset-0 bg-[#0c1220]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_100%,rgba(34,211,238,0.15),transparent_50%)]" />
        
        <div className="relative z-10 w-full max-w-md">
          {authState === 'login' && (
            <LoginForm 
              onSuccess={handleLoginSuccess}
              onForgotPassword={() => setAuthState('forgot-password')}
              onSSOLogin={() => setAuthState('sso-login')}
            />
          )}

          {authState === 'sso-login' && (
            <SSOLoginForm
              onBack={handleBackToLogin}
              onSSORedirect={handleSSORedirect}
              parRedirectUri={getEffectiveRedirectUri()}
            />
          )}
          
          {authState === 'forgot-password' && (
            <ForgotPasswordForm 
              onSuccess={handleResetSent}
              onBack={handleBackToLogin}
            />
          )}
          
          {authState === 'reset-sent' && (
            <div className="w-full space-y-8">
              {/* Left aligned like other auth forms */}
              <div className="flex flex-col items-start space-y-6">
                <Logo size="lg" />
                <div className="text-left space-y-2">
                  <h1 className="text-2xl font-realhead tracking-tight text-white">Check your email</h1>
                  <p className="text-gray-400">
                    We've sent a password reset link to your email address. Please check your inbox and follow the instructions.
                  </p>
                </div>
              </div>
              
              <div className="space-y-4">
                <button 
                  onClick={handleResendResetEmail}
                  disabled={resendLoading || resendCooldown > 0}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldown > 0 ? (
                    `Resend available in ${resendCooldown}s`
                  ) : (
                    "Didn't receive the email? Resend"
                  )}
                </button>
                
                <div className="text-center">
                  <button 
                    onClick={handleBackToLogin}
                    className="text-primary hover:underline text-sm"
                  >
                    Back to sign in
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {authState === 'reset-password' && (
            <ResetPasswordForm 
              onSuccess={handlePasswordResetSuccess}
              onBack={handleBackToLogin}
              email={resetEmail || undefined}
              token={resetToken || undefined}
            />
          )}
          
          {authState === '2fa-verify' && factorId && (
            <MFAVerifyForm 
              factorId={factorId}
              onSuccess={handleMFAVerifySuccess}
              onSignOut={handleBackToLogin}
            />
          )}
          
          {authState === '2fa-setup' && (
            <MFASetupForm 
              onComplete={handleMFASetupComplete}
              onSkip={handleMFASkip}
            />
          )}
        </div>
      </div>
    </div>
    </>
  );
}
